/**
 * Data freshness for the PulseSync badge (BUILD-PLAN 2.5.6). Pure so the
 * thresholds are testable and the badge never computes time in render.
 */
export type FreshnessLevel = 'live' | 'recent' | 'stale' | 'offline' | 'unknown';

export interface FreshnessThresholds {
  /** At or under this age (seconds) the data counts as live. */
  liveSec: number;
  /** Over this age the data is stale (amber). */
  staleSec: number;
  /** Over this age the source is treated as offline (red). */
  offlineSec: number;
}

export const DEFAULT_FRESHNESS: FreshnessThresholds = {
  liveSec: 60,
  staleSec: 300,
  offlineSec: 1800,
};

/**
 * Thresholds for a PulseSync office whose connector syncs every
 * `intervalSec` (the API's one sync rule, shell/freshness.ts `syncLevel`):
 * amber past 2x the interval plus 2 minutes, red past 4x plus 2 minutes.
 * On a 5-minute connector "Synced 4m ago" is normal, not late.
 */
export function connectorFreshness(intervalSec: number): FreshnessThresholds {
  const grace = 120;
  return {
    liveSec: DEFAULT_FRESHNESS.liveSec,
    staleSec: 2 * intervalSec + grace,
    offlineSec: 4 * intervalSec + grace,
  };
}

export function freshnessLevel(
  lastSyncedAt: Date | string | number | null | undefined,
  now: Date | number,
  t: FreshnessThresholds = DEFAULT_FRESHNESS,
): FreshnessLevel {
  if (lastSyncedAt === null || lastSyncedAt === undefined) return 'unknown';
  const last = new Date(lastSyncedAt).getTime();
  if (!Number.isFinite(last)) return 'unknown';
  const age = Math.max(0, (new Date(now).getTime() - last) / 1000);
  if (age <= t.liveSec) return 'live';
  if (age <= t.staleSec) return 'recent';
  if (age <= t.offlineSec) return 'stale';
  return 'offline';
}

/** "just now", "45s ago", "12m ago", "3h ago", "2d ago". */
export function relativeAge(lastSyncedAt: Date | string | number, now: Date | number): string {
  const sec = Math.max(
    0,
    Math.round((new Date(now).getTime() - new Date(lastSyncedAt).getTime()) / 1000),
  );
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
